import api from '@/api/axiosApi'
import type { DagTableLoadResponse } from '@/types/dag'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChildSummary {
  id: string
  plan_sku_num?: string | null
  description?: string | null
  inner_pack?: number | null
}

export interface ChildrenResponse {
  parent_line_item_id: string
  children_count: number
  children: ChildSummary[]
}

export interface PIABEntryRead {
  id: string
  child_line_item_id: string
  plan_sku_num?: string | null
  description?: string | null
  inner_pack?: number | null
  units: number
}

export interface PIABResponse {
  parent_line_item_id: string
  items: PIABEntryRead[]
}

export interface PIABItemCreate {
  child_line_item_id: string
  units: number
}

export interface ReplacePIABRequest {
  items: PIABItemCreate[]
}

// ---------------------------------------------------------------------------
// Children endpoints
// ---------------------------------------------------------------------------

export async function fetchChildren(
  planId: string,
  versionId: string,
  lineId: string
): Promise<ChildrenResponse> {
  const { data } = await api.get<ChildrenResponse>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/children`
  )
  return data
}

export async function assignChildren(
  planId: string,
  versionId: string,
  lineId: string,
  body: { child_line_item_ids: string[]; keep_piab_quantities?: boolean }
): Promise<ChildrenResponse> {
  const { data } = await api.post<ChildrenResponse>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/children`,
    body
  )
  return data
}

export async function reassignChildren(
  planId: string,
  versionId: string,
  lineId: string,
  body: { child_line_item_ids: string[]; keep_piab_quantities?: boolean }
): Promise<ChildrenResponse> {
  const { data } = await api.put<ChildrenResponse>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/children`,
    body
  )
  return data
}

export async function removeChildren(
  planId: string,
  versionId: string,
  lineId: string,
  body: { child_line_item_ids: string[] }
): Promise<void> {
  await api.delete(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/children`,
    { data: body }
  )
}

// ---------------------------------------------------------------------------
// PIAB endpoints
// ---------------------------------------------------------------------------

export async function fetchPIAB(
  planId: string,
  versionId: string,
  lineId: string
): Promise<PIABResponse> {
  const { data } = await api.get<PIABResponse>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/masterpack`
  )
  return data
}

export async function replacePIAB(
  planId: string,
  versionId: string,
  lineId: string,
  body: ReplacePIABRequest
): Promise<PIABResponse> {
  const { data } = await api.put<PIABResponse>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/masterpack`,
    body
  )
  return data
}

export async function deletePIAB(
  planId: string,
  versionId: string,
  lineId: string
): Promise<void> {
  await api.delete(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/masterpack`
  )
}

// ---------------------------------------------------------------------------
// Parent line search (reuses DAG rows endpoint)
// ---------------------------------------------------------------------------

export async function searchParentLines(
  planId: string,
  versionId: string,
  query: string
): Promise<DagTableLoadResponse> {
  const { data } = await api.get<DagTableLoadResponse>(
    `/plans/${planId}/versions/${versionId}/table/rows`,
    {
      params: {
        ...(query ? { q: query } : {}),
        sku_level: 'Parent',
        limit: 20,
        offset: 0,
        include_aggregates: false,
      },
    }
  )
  return data
}
